/**
 * EventStore API client for interacting with event streams
 */
import type { HttpClient } from '../utils/http';

import type {
  Event,
  EventDataOf,
  EventMetadataOf,
  EventTypeOf,
  ReadEvent,
  StreamId,
  StreamPosition,
} from './types/event-types';

import type {
  AggregateStreamOptions,
  AggregateStreamResult,
  AppendToStreamOptions,
  AppendToStreamResult,
  EventStore as EventStoreInterface,
  ReadStreamOptions,
  ReadStreamResult,
} from './types/store-interfaces';

/**
 * Request for appending events to a stream
 * @internal
 */
interface AppendEventsRequest {
  events: Event[];
  options?: {
    expectedStreamVersion?: number | 'no_stream' | 'stream_exists' | 'any';
  };
}

/**
 * Response from appending events to a stream
 * @internal
 */
interface AppendEventsResponse {
  success: boolean;
  nextExpectedStreamVersion: number;
  eventsWritten?: number;
}

/**
 * API response type for reading a stream
 */
type ApiReadStreamResponse<EventType extends Event = Event> = {
  events: Array<ReadEvent<EventType>>;
  currentStreamVersion: StreamPosition;
  streamExists?: boolean;
};

/**
 * API response type for querying events
 */
type ApiQueryEventsResponse<EventType extends Event = Event> = {
  events: Array<ReadEvent<EventType>>;
  pagination: {
    limit: number;
    offset: number;
    total?: number;
    hasMore: boolean;
  };
};

/**
 * API response type for querying streams
 */
type ApiQueryStreamsResponse = {
  streams: Array<StreamInfo>;
  pagination: {
    limit: number;
    offset: number;
    total?: number;
    hasMore: boolean;
  };
};

/**
 * Stream information returned from a query
 */
export interface StreamInfo {
  streamId: string;
  streamPosition: StreamPosition;
  streamType: string;
  streamMetadata: Record<string, unknown>;
  isArchived: boolean;
  lastArchivedPosition: StreamPosition;
  createdAt: string;
  updatedAt: string;
}

/**
 * Query parameters for filtering events
 */
export interface QueryEventsOptions {
  streamId?: string;
  type?: string | string[];
  eventId?: string;
  transactionId?: string;
  fromPosition?: number;
  toPosition?: number;
  fromDate?: string;
  toDate?: string;
  phase?: number;
  includeArchived?: boolean;
  limit?: number;
  offset?: number;
  sortBy?: 'globalPosition' | 'createdAt' | 'streamPosition';
  sortDirection?: 'asc' | 'desc';
  includeCount?: boolean;
}

/**
 * Query parameters for filtering streams
 */
export interface QueryStreamsOptions {
  streamId?: string;
  streamType?: string | string[];
  streamIdPattern?: string;
  minPosition?: number;
  maxPosition?: number;
  fromDate?: string;
  toDate?: string;
  includeArchived?: boolean;
  includeMetadata?: boolean;
  limit?: number;
  offset?: number;
  sortBy?: 'streamPosition' | 'createdAt' | 'updatedAt' | 'streamId';
  sortDirection?: 'asc' | 'desc';
  includeCount?: boolean;
}

/**
 * Result of a query events operation
 */
export interface QueryEventsResult<EventType extends Event = Event> {
  events: ReadEvent<EventType>[];
  pagination: {
    limit: number;
    offset: number;
    total?: number;
    hasMore: boolean;
  };
}

/**
 * Result of a query streams operation
 */
export interface QueryStreamsResult {
  streams: StreamInfo[];
  pagination: {
    limit: number;
    offset: number;
    total?: number;
    hasMore: boolean;
  };
}

/**
 * Implementation of the EventStore interface for DeltaBase
 */
export class EventStore implements EventStoreInterface {
  private http: HttpClient;
  private eventStoreName: string;

  /**
   * Creates a new EventStore client instance
   *
   * @param http - The HTTP client to use for API requests
   * @param eventStoreName - The name of the event store to interact with
   */
  constructor(http: HttpClient, eventStoreName: string) {
    this.http = http;
    this.eventStoreName = eventStoreName;
  }

  /**
   * Aggregate events from a stream and compute a state
   *
   * @param streamId - The ID of the stream to aggregate events from
   * @param options - Configuration options for the aggregation process
   * @param options.initialState - Function that returns the initial state
   * @param options.evolve - Function that applies an event to the current state
   * @param options.read - Optional read options to control which events to include
   * @returns Promise resolving to the aggregation result with the computed state and stream metadata
   *
   * @example
   * ```typescript
   * // Define your state type and event types
   * type UserState = { email: string, isVerified: boolean };
   * type UserEvent =
   *   | { type: 'user.created', data: { email: string } }
   *   | { type: 'user.verified', data: { verifiedAt: string } };
   *
   * // Aggregate the stream into a state
   * const result = await eventStore.aggregateStream<UserState, UserEvent>(
   *   'user-123',
   *   {
   *     initialState: () => ({ email: '', isVerified: false }),
   *     evolve: (state, event) => {
   *       switch (event.type) {
   *         case 'user.created':
   *           return { ...state, email: event.data.email };
   *         case 'user.verified':
   *           return { ...state, isVerified: true };
   *         default:
   *           return state;
   *       }
   *     },
   *     read: { from: 0 }
   *   }
   * );
   * ```
   */
  async aggregateStream<State, EventType extends Event>(
    streamId: string,
    options: AggregateStreamOptions<State, EventType>
  ): Promise<AggregateStreamResult<State>> {
    // Read the events first
    const readResult = await this.readStream<EventType>(streamId, options.read);

    // Apply the events to the initial state
    let state = options.initialState();
    for (const event of readResult.events) {
      state = options.evolve(state, event);
    }

    return {
      currentStreamVersion: readResult.currentStreamVersion,
      state,
      streamExists: readResult.streamExists,
    };
  }

  /**
   * Read events from a stream
   *
   * @param streamId - The ID of the stream to read events from
   * @param options - The options for reading events
   * @param options.from - Optional starting position to read from
   * @param options.to - Optional ending position to read to
   * @param options.maxCount - Optional maximum number of events to read
   * @param options.expectedStreamVersion - Optional expected version for optimistic concurrency
   * @returns Promise resolving to the read result containing events and stream metadata
   *
   * @example
   * ```typescript
   * // Read all events from a stream
   * const result = await eventStore.readStream('user-123');
   *
   * // Read events with a specific starting position
   * const result = await eventStore.readStream('user-123', { from: 5 });
   *
   * // Read a specific range of events
   * const result = await eventStore.readStream('user-123', { from: 5, to: 10 });
   *
   * // Read a limited number of events
   * const result = await eventStore.readStream('user-123', { maxCount: 100 });
   * ```
   */
  async readStream<EventType extends Event>(
    streamId: string,
    options?: ReadStreamOptions
  ): Promise<ReadStreamResult<EventType>> {
    const url = `/api/event-stores/${this.eventStoreName}/streams/${streamId}`;

    // Convert interface options to API options
    const apiOptions: Record<string, string> = {};

    if (options) {
      if ('from' in options) {
        apiOptions.from = options.from.toString();
      }

      if ('to' in options) {
        apiOptions.to = options.to.toString();
      }

      if ('maxCount' in options && options.maxCount !== undefined) {
        apiOptions.maxCount = options.maxCount.toString();
      }

      if (
        'expectedStreamVersion' in options &&
        options.expectedStreamVersion !== undefined
      ) {
        apiOptions.expectedStreamVersion =
          options.expectedStreamVersion.toString();
      }
    }

    const response = await this.http.get<ApiReadStreamResponse>(
      url,
      apiOptions
    );

    // Map the events from API format to our SDK format
    const mappedEvents = (response.events || []).map((event) => ({
      type: event.type as EventTypeOf<EventType>,
      data: event.data as EventDataOf<EventType>,
      streamId: event.streamId,
      streamPosition: event.streamPosition,
      globalPosition: event.globalPosition,
      eventId: event.eventId || 'unknown',
      schemaVersion: event.schemaVersion || '1.0.0',
      transactionId: event.transactionId || '0',
      createdAt: event.createdAt || new Date().toISOString(),
      metadata: event.metadata || {},
    }));

    // Return the properly typed result
    return {
      currentStreamVersion: response.currentStreamVersion,
      events: mappedEvents as ReadEvent<EventType>[],
      streamExists: response.streamExists ?? true,
    };
  }

  /**
   * Append events to a stream
   *
   * @param streamId - The ID of the stream to append events to
   * @param events - Array of events to append to the stream
   * @param options - Optional parameters for the append operation
   * @param options.expectedStreamVersion - Optional expected version for optimistic concurrency
   * @returns Promise resolving to the append result with the next expected version
   * @throws {VersionConflictError} When expectedStreamVersion doesn't match current stream version
   * @throws {ValidationError} When request validation fails
   * @throws {EventStoreNotFoundError} When the event store doesn't exist
   * @throws {AuthenticationError} When authentication fails
   *
   * @example
   * ```typescript
   * import {
   *   isVersionConflictError,
   *   isValidationError
   * } from '@delta-base/server';
   *
   * try {
   *   // Append with optimistic concurrency control
   *   await eventStore.appendToStream(
   *     'user-123',
   *     [{
   *       type: 'user.updated',
   *       data: { email: 'updated@example.com' }
   *     }],
   *     { expectedStreamVersion: 0n }
   *   );
   * } catch (error) {
   *   if (isVersionConflictError(error)) {
   *     console.log(`Version conflict: expected ${error.expectedVersion}, got ${error.currentVersion}`);
   *     // Handle concurrency conflict
   *   } else if (isValidationError(error)) {
   *     console.log('Validation errors:', error.validationErrors);
   *     // Handle validation failures
   *   } else {
   *     throw error; // Re-throw unknown errors
   *   }
   * }
   * ```
   */
  async appendToStream<EventType extends Event>(
    streamId: string,
    events: EventType[],
    options?: AppendToStreamOptions
  ): Promise<AppendToStreamResult> {
    const request: AppendEventsRequest = {
      events,
      options: {
        expectedStreamVersion:
          options?.expectedStreamVersion !== undefined
            ? typeof options.expectedStreamVersion === 'number'
              ? Number(options.expectedStreamVersion)
              : typeof options.expectedStreamVersion === 'string'
                ? options.expectedStreamVersion === 'no_stream'
                  ? 'no_stream'
                  : options.expectedStreamVersion === 'stream_exists'
                    ? 'stream_exists'
                    : 'any'
                : Number(options.expectedStreamVersion)
            : undefined,
      },
    };

    const response = await this.http.post<AppendEventsResponse>(
      `/api/event-stores/${this.eventStoreName}/streams/${streamId}`,
      request as unknown as Record<string, unknown>
    );

    return {
      nextExpectedStreamVersion: response.nextExpectedStreamVersion,
      createdNewStream: !response.success,
    };
  }

  /**
   * Query events with flexible filtering options
   *
   * @param options - Query parameters for filtering events
   * @param options.streamId - Optional stream ID to filter by
   * @param options.type - Optional event type(s) to filter by
   * @param options.eventId - Optional specific event ID to retrieve
   * @param options.transactionId - Optional transaction ID to filter by
   * @param options.fromPosition - Optional starting global position
   * @param options.toPosition - Optional ending global position
   * @param options.fromDate - Optional starting date for time-based filtering
   * @param options.toDate - Optional ending date for time-based filtering
   * @param options.phase - Optional event phase to filter by
   * @param options.includeArchived - Whether to include archived events
   * @param options.limit - Maximum number of events to return
   * @param options.offset - Number of events to skip
   * @param options.sortBy - Field to sort results by
   * @param options.sortDirection - Direction to sort results
   * @param options.includeCount - Whether to include total count in results
   * @returns Promise resolving to the query result with events and pagination info
   *
   * @example
   * ```typescript
   * // Query all events of a specific type
   * const result = await eventStore.queryEvents({
   *   type: 'user.created'
   * });
   *
   * // Query events with pagination
   * const result = await eventStore.queryEvents({
   *   limit: 20,
   *   offset: 40,
   *   includeCount: true
   * });
   *
   * // Query events within a time range
   * const result = await eventStore.queryEvents({
   *   fromDate: '2023-01-01T00:00:00Z',
   *   toDate: '2023-01-31T23:59:59Z'
   * });
   * ```
   */
  async queryEvents<EventType extends Event = Event>(
    options: QueryEventsOptions = {}
  ): Promise<QueryEventsResult<EventType>> {
    const url = `/api/event-stores/${this.eventStoreName}/events`;

    // Convert options to API query parameters
    const queryParams: Record<string, string> = {};

    for (const [key, value] of Object.entries(options)) {
      if (value !== undefined) {
        if (Array.isArray(value)) {
          queryParams[key] = value.join(',');
        } else {
          queryParams[key] = String(value);
        }
      }
    }

    const response = await this.http.get<ApiQueryEventsResponse>(
      url,
      queryParams
    );

    // Transform the response events to our SDK types
    return {
      events: (response.events || []).map(
        (event) =>
          ({
            type: event.type,
            data: event.data as EventDataOf<EventType>,
            streamId: event.streamId as StreamId,
            streamPosition: Number(event.streamPosition || 0),
            globalPosition: Number(event.globalPosition || 0),
            eventId: event.eventId || 'unknown',
            schemaVersion: event.schemaVersion || '1.0.0',
            transactionId: event.transactionId || '0',
            createdAt: event.createdAt || new Date().toISOString(),
            metadata: event.metadata as EventMetadataOf<EventType>,
          }) as ReadEvent<EventType>
      ),
      pagination: {
        limit: response.pagination.limit,
        offset: response.pagination.offset,
        total: response.pagination.total,
        hasMore: response.pagination.hasMore,
      },
    };
  }

  /**
   * Query events for a specific stream with filtering options
   *
   * @param streamId - The ID of the stream to query events from
   * @param options - Query parameters for filtering events
   * @param options.type - Optional event type(s) to filter by
   * @param options.eventId - Optional specific event ID to retrieve
   * @param options.transactionId - Optional transaction ID to filter by
   * @param options.fromPosition - Optional starting global position
   * @param options.toPosition - Optional ending global position
   * @param options.fromDate - Optional starting date for time-based filtering
   * @param options.toDate - Optional ending date for time-based filtering
   * @param options.phase - Optional event phase to filter by
   * @param options.includeArchived - Whether to include archived events
   * @param options.limit - Maximum number of events to return
   * @param options.offset - Number of events to skip
   * @param options.sortBy - Field to sort results by
   * @param options.sortDirection - Direction to sort results
   * @param options.includeCount - Whether to include total count in results
   * @returns Promise resolving to the query result with events and pagination info
   *
   * @example
   * ```typescript
   * // Query events for a specific stream
   * const result = await eventStore.queryStreamEvents('user-123', {
   *   type: 'user.updated'
   * });
   * ```
   */
  async queryStreamEvents<EventType extends Event = Event>(
    streamId: string,
    options: Omit<QueryEventsOptions, 'streamId'> = {}
  ): Promise<QueryEventsResult<EventType>> {
    return this.queryEvents<EventType>({
      ...options,
      streamId,
    });
  }

  /**
   * Query streams with filtering options
   *
   * @param options - Query parameters for filtering streams
   * @param options.streamId - Optional specific stream ID to retrieve
   * @param options.streamType - Optional stream type(s) to filter by
   * @param options.streamIdPattern - Optional pattern to match stream IDs
   * @param options.minPosition - Optional minimum stream position
   * @param options.maxPosition - Optional maximum stream position
   * @param options.fromDate - Optional starting date for time-based filtering
   * @param options.toDate - Optional ending date for time-based filtering
   * @param options.includeArchived - Whether to include archived streams
   * @param options.includeMetadata - Whether to include stream metadata
   * @param options.limit - Maximum number of streams to return
   * @param options.offset - Number of streams to skip
   * @param options.sortBy - Field to sort results by
   * @param options.sortDirection - Direction to sort results
   * @param options.includeCount - Whether to include total count in results
   * @returns Promise resolving to the query result with streams and pagination info
   *
   * @example
   * ```typescript
   * // Query all streams
   * const result = await eventStore.queryStreams();
   *
   * // Query streams by type
   * const result = await eventStore.queryStreams({
   *   streamType: 'user'
   * });
   *
   * // Query streams with a pattern match
   * const result = await eventStore.queryStreams({
   *   streamIdPattern: 'user-%'
   * });
   * ```
   */
  async queryStreams(
    options: QueryStreamsOptions = {}
  ): Promise<QueryStreamsResult> {
    const url = `/api/event-stores/${this.eventStoreName}/streams`;

    // Convert options to API query parameters
    const queryParams: Record<string, string> = {};

    for (const [key, value] of Object.entries(options)) {
      if (value !== undefined) {
        if (Array.isArray(value)) {
          queryParams[key] = value.join(',');
        } else {
          queryParams[key] = String(value);
        }
      }
    }

    const response = await this.http.get<ApiQueryStreamsResponse>(
      url,
      queryParams
    );

    return {
      streams: (response.streams || []).map((stream) => ({
        streamId: stream.streamId,
        streamPosition: stream.streamPosition,
        streamType: stream.streamType,
        streamMetadata: stream.streamMetadata || {},
        isArchived: stream.isArchived,
        lastArchivedPosition: stream.lastArchivedPosition,
        createdAt: stream.createdAt,
        updatedAt: stream.updatedAt,
      })),
      pagination: {
        limit: response.pagination.limit,
        offset: response.pagination.offset,
        total: response.pagination.total,
        hasMore: response.pagination.hasMore,
      },
    };
  }

  /**
   * Query streams of a specific type with filtering options
   *
   * @param streamType - The stream type to filter by
   * @param options - Query parameters for filtering streams
   * @param options.streamId - Optional specific stream ID to retrieve
   * @param options.streamIdPattern - Optional pattern to match stream IDs
   * @param options.minPosition - Optional minimum stream position
   * @param options.maxPosition - Optional maximum stream position
   * @param options.fromDate - Optional starting date for time-based filtering
   * @param options.toDate - Optional ending date for time-based filtering
   * @param options.includeArchived - Whether to include archived streams
   * @param options.includeMetadata - Whether to include stream metadata
   * @param options.limit - Maximum number of streams to return
   * @param options.offset - Number of streams to skip
   * @param options.sortBy - Field to sort results by
   * @param options.sortDirection - Direction to sort results
   * @param options.includeCount - Whether to include total count in results
   * @returns Promise resolving to the query result with streams and pagination info
   *
   * @example
   * ```typescript
   * // Query all user streams
   * const result = await eventStore.queryStreamsByType('user');
   *
   * // Query user streams with pagination
   * const result = await eventStore.queryStreamsByType('user', {
   *   limit: 20,
   *   offset: 0
   * });
   * ```
   */
  async queryStreamsByType(
    streamType: string,
    options: Omit<QueryStreamsOptions, 'streamType'> = {}
  ): Promise<QueryStreamsResult> {
    return this.queryStreams({
      ...options,
      streamType,
    });
  }

  /**
   * Get a list of stream IDs in an event store
   *
   * @param options - Optional parameters for listing streams
   * @param options.limit - Maximum number of stream IDs to return
   * @param options.offset - Number of stream IDs to skip
   * @param options.pattern - Pattern to match stream IDs (e.g., 'user-*')
   * @returns Promise resolving to an object containing stream IDs and total count
   *
   * @example
   * ```typescript
   * // List all streams
   * const { streams, total } = await eventStore.listStreams();
   *
   * // List streams with pagination
   * const { streams, total } = await eventStore.listStreams({
   *   limit: 50,
   *   offset: 100
   * });
   *
   * // List streams matching a pattern
   * const { streams, total } = await eventStore.listStreams({
   *   pattern: 'user-*'
   * });
   * ```
   */
  async listStreams(options?: {
    limit?: number;
    offset?: number;
    pattern?: string;
  }): Promise<{ streams: string[]; total: number }> {
    const params: Record<string, string> = {};

    if (options?.limit) {
      params.limit = options.limit.toString();
    }

    if (options?.offset) {
      params.offset = options.offset.toString();
    }

    if (options?.pattern) {
      params.pattern = options.pattern;
    }

    return this.http.get<{ streams: string[]; total: number }>(
      `/api/event-stores/${this.eventStoreName}/streams`,
      params
    );
  }
}
